import React, { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { TECH_MODULES, TECH_MOCKUPS, TECH_PERFECT } from "../data/techAtmosphere";
import { Ornament } from "./Ornament";

/**
 * Section: "Technology & Atmosphere"
 * Includes:
 *  - Editorial mockup collage (4 immersive-*.png)
 *  - 15 modules from Innovation & Immersive Brand Experiences
 *  - Perfect-for tag & CTA
 */
export default function ImmersivePin() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setVisible(true)),
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="immersive-pin"
      className="relative bg-[var(--lu-black)] grain overflow-hidden"
      data-testid="tech-atmosphere-section"
    >
      {/* Golden lamp beams */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center" aria-hidden>
        <div
          className="h-64 w-[38rem] opacity-30"
          style={{
            backgroundImage:
              "conic-gradient(from 70deg at center top, #C9A050, transparent, transparent)",
          }}
        />
        <div
          className="h-64 w-[38rem] opacity-30 -ml-[38rem]"
          style={{
            backgroundImage:
              "conic-gradient(from 290deg at center top, transparent, transparent, #C9A050)",
          }}
        />
      </div>

      <div className="container-lux relative z-10 py-24 lg:py-32">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="kicker text-[var(--lu-gold)]">Technology &amp; Atmosphere</p>
          <h2 className="mt-6 h-editorial text-[42px] sm:text-[58px] lg:text-[80px] text-[var(--lu-ivory)] leading-[1.02]">
            Innovation{" "}
            <span className="italic text-[var(--lu-gold)]">Tech</span>{" "}
            Entertainment Experiences
          </h2>
          <Ornament className="my-10" light />
          <p className="text-[15px] leading-[1.9] text-ivory-75 font-light">
            AI · Digital Art · Interactive Technology · Projection Mapping · Holograms ·
            Motion Tracking · LED Shows · Drone Experiences · Kinetic Installations ·
            Future Entertainment. A complete framework of high-tech immersive shows,
            designed for brands that want to leave the most cinematic imprint.
          </p>
        </div>

        {/* Mockup collage — 4 images */}
        <div
          className={`mt-20 grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          data-testid="tech-mockup-collage"
        >
          {TECH_MOCKUPS.map((src, i) => (
            <figure
              key={src}
              className="relative overflow-hidden border border-[var(--lu-gold)]/30 group"
              style={{
                aspectRatio: "3 / 4",
                transitionDelay: `${i * 120}ms`,
              }}
              data-testid={`tech-mockup-${i}`}
            >
              <img
                src={src}
                alt={`Innovation Tech Entertainment mockup ${i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--lu-black)]/60 via-transparent to-transparent" />
              <figcaption className="absolute bottom-3 left-4 font-micro text-[10px] tracking-[0.28em] text-[var(--lu-gold)]/85 uppercase">
                Case {String(i + 1).padStart(2, "0")}
              </figcaption>
            </figure>
          ))}
        </div>

        {/* 15 Modules grid */}
        <div className="mt-24 lg:mt-32">
          <div className="text-center">
            <p className="kicker text-[var(--lu-gold)]">15 Signature Modules</p>
            <h3 className="mt-4 h-editorial text-[30px] sm:text-[42px] lg:text-[56px] text-[var(--lu-ivory)] leading-[1.05]">
              The full <span className="italic text-[var(--lu-gold)]">tech-arsenal</span>
            </h3>
          </div>

          <div
            className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--lu-gold)]/20"
            data-testid="tech-modules-grid"
          >
            {TECH_MODULES.map((m) => (
              <article
                key={m.n}
                className="bg-[var(--lu-black)] p-8 lg:p-10 hover:bg-[#0f0f10] transition-colors duration-500"
                data-testid={`tech-module-${m.n}`}
              >
                <div className="flex items-start gap-4">
                  <span className="font-micro text-[11px] tracking-[0.32em] text-[var(--lu-gold)] pt-1">
                    {m.n}
                  </span>
                  <div>
                    <h4 className="h-editorial text-[22px] lg:text-[26px] text-[var(--lu-ivory)] leading-[1.15]">
                      {m.title}
                    </h4>
                    <p className="mt-4 text-[14px] leading-[1.85] text-ivory-75 font-light">
                      {m.body}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Perfect-for + CTA */}
        <div className="mt-20 text-center max-w-3xl mx-auto">
          <p className="kicker text-[var(--lu-gold)] mb-6">Perfect for</p>
          <p className="text-[13px] tracking-[0.14em] uppercase text-ivory-75 leading-[1.9]">
            {TECH_PERFECT}
          </p>
          <div className="mt-12 flex items-center justify-center gap-2 text-[var(--lu-gold)]">
            <Sparkles size={16} />
            <span className="font-micro text-[10px] tracking-[0.32em] uppercase">
              Bespoke Innovation Briefs
            </span>
            <Sparkles size={16} />
          </div>
          <div className="mt-10">
            <a
              href="/book-a-call"
              className="btn-gold"
              data-testid="tech-atmosphere-cta"
            >Request an Innovation Brief</a>
          </div>
        </div>
      </div>
    </section>
  );
}
